import Phaser from 'phaser';
import { regions } from './layout';
import { makeTwoPassText, type TwoPassText } from './twoPassText';

export class FoodBonus {
  private foodSprite: Phaser.GameObjects.Image | null = null;
  private banner: TwoPassText | null = null;
  private timerEvent: Phaser.Time.TimerEvent | null = null;
  private remainingSeconds = 0;

  constructor(
    private scene: Phaser.Scene,
    private boardCellCenter: (idx: number) => { x: number; y: number },
    private cellSize: number,
  ) {}

  spawnAt(cellIndex: number, durationSec: number, onExpire: () => void): void {
    this.clear();
    const { x, y } = this.boardCellCenter(cellIndex);
    // dogbonus.png is the iOS food-bonus art (382×324). Centered on a single
    // cell and sized to fit with a small inset.
    const size = this.cellSize * 0.9;
    // Not interactive: the scene-level cell tap handler runs placement
    // logic; the bonus is collected only via a successful placement on
    // the bonus square (mirrors iOS Board.swift:2027 — bonusSquare check
    // sits inside the placement path, not a separate tap handler).
    this.foodSprite = this.scene.add
      .image(x, y, 'dogbonus')
      .setDepth(2000)
      .setOrigin(0.5)
      .setDisplaySize(size, size);

    this.remainingSeconds = durationSec;
    // Banner: two-pass Snacker Comic, same font height as the header score,
    // bottom-baseline aligned to the score's baseline, left-aligned to the
    // Big Puppy panel's left edge (header.x + 15).
    const header = regions().header;
    const bannerX = header.x + 15;
    const bannerY = header.y + header.height - 9;
    this.banner = makeTwoPassText(this.scene, bannerX, bannerY, this.bannerText(), {
      fontSize: '48px',
      originX: 0,
      originY: 1,
      depth: 2001,
    });

    this.timerEvent = this.scene.time.addEvent({
      delay: 1000,
      repeat: durationSec - 1,
      callback: () => {
        this.remainingSeconds -= 1;
        if (this.banner) this.banner.setText(this.bannerText());
        if (this.remainingSeconds <= 0) {
          this.clear();
          onExpire();
        }
      },
    });
  }

  // Play the bonus-collected flyaway tween (food zips to the upper-right
  // corner where the CrazySquares badge lives) then destroy the sprite +
  // banner + timer. Called by MainScene after a successful placement on
  // the bonus square.
  collect(onCollected: () => void): void {
    const food = this.foodSprite;
    if (!food) { onCollected(); return; }
    this.scene.tweens.add({
      targets: food,
      x: this.scene.cameras.main.width - 100,
      y: 200,
      scale: 0.1,
      duration: 500,
      ease: 'Cubic.In',
      onComplete: () => {
        food.destroy();
        this.foodSprite = null;
        this.banner?.destroy();
        this.banner = null;
        this.timerEvent?.remove();
        this.timerEvent = null;
        onCollected();
      },
    });
  }

  clear(): void {
    this.foodSprite?.destroy();
    this.foodSprite = null;
    this.banner?.destroy();
    this.banner = null;
    this.timerEvent?.remove();
    this.timerEvent = null;
  }

  private bannerText(): string {
    return `Bonus! Get the food! ${this.remainingSeconds}`;
  }
}
